import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { toast } from "sonner";
import { Plus, Trash2, Loader2, Search, ArrowLeft, Edit } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";

interface DelegationSetting {
    id: number;
    name: string;
    type: string;
    category: string;
}

export default function DelegationSettingsPage() {
    const { token } = useAuth();
    const navigate = useNavigate();
    const [settings, setSettings] = useState<DelegationSetting[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");

    // New item state
    const [newName, setNewName] = useState("");
    const [newType, setNewType] = useState("Task");
    const [newCategory, setNewCategory] = useState("");

    // Edit state
    const [editingItem, setEditingItem] = useState<DelegationSetting | null>(null);
    const [editName, setEditName] = useState("");
    const [editType, setEditType] = useState("Task");
    const [editCategory, setEditCategory] = useState("");
    const [isEditOpen, setIsEditOpen] = useState(false);

    const fetchSettings = async () => {
        try {
            setLoading(true);
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation-settings`, {
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });
            const data = await response.json();
            if (data.success) {
                setSettings(data.settings);
            }
        } catch (error) {
            console.error("Error fetching settings:", error);
            toast.error("Failed to load settings");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchSettings();
    }, [token]);

    const handleAdd = async () => {
        if (!newName.trim()) {
            toast.error("Name is required");
            return;
        }

        try {
            setSaving(true);
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation-settings`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: newName,
                    type: newType,
                    category: newCategory
                })
            });

            const data = await response.json();
            if (data.success) {
                toast.success("Item added successfully");
                setNewName("");
                setNewCategory("");
                setNewType("Task");
                fetchSettings();
            } else {
                toast.error(data.message || "Failed to add item");
            }
        } catch (error) {
            console.error("Error adding item:", error);
            toast.error("Error adding item");
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation-settings/${id}`, {
                method: "DELETE",
                headers: {
                    "Authorization": `Bearer ${token}`
                }
            });

            if (response.ok) {
                toast.success("Item deleted");
                fetchSettings();
            } else {
                toast.error("Failed to delete item");
            }
        } catch (error) {
            console.error("Error deleting item:", error);
            toast.error("Error deleting item");
        }
    };

    const handleEditClick = (item: DelegationSetting) => {
        setEditingItem(item);
        setEditName(item.name);
        setEditType(item.type);
        setEditCategory(item.category || "");
        setIsEditOpen(true);
    };

    const handleUpdate = async () => {
        if (!editName.trim()) {
            toast.error("Name is required");
            return;
        }

        try {
            setSaving(true);
            const response = await fetch(`${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5001'}/api/delegation-settings/${editingItem?.id}`, {
                method: "PATCH",
                headers: {
                    "Content-Type": "application/json",
                    "Authorization": `Bearer ${token}`
                },
                body: JSON.stringify({
                    name: editName,
                    type: editType,
                    category: editCategory
                })
            });

            const data = await response.json();
            if (data.success) {
                toast.success("Item updated successfully");
                setIsEditOpen(false);
                fetchSettings();
            } else {
                toast.error(data.message || "Failed to update item");
            }
        } catch (error) {
            console.error("Error updating item:", error);
            toast.error("Error updating item");
        } finally {
            setSaving(false);
        }
    };

    const filteredSettings = settings.filter(s =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (s.category && s.category.toLowerCase().includes(searchTerm.toLowerCase()))
    );

    return (
        <div className="space-y-6">
            <div className="flex items-center gap-4">
                <Button variant="ghost" size="icon" onClick={() => navigate('/development')}>
                    <ArrowLeft size={20} />
                </Button>
                <div>
                    <h1 className="text-2xl font-bold">Delegation Settings</h1>
                    <p className="text-muted-foreground text-sm">
                        Manage predefined Tasks, Activities, and Decisions available for delegation.
                    </p>
                </div>
            </div>

            <Card>
                <CardHeader>
                    <CardTitle>Add Predefined Task</CardTitle>
                </CardHeader>
                <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-4 items-end">
                        <div className="md:col-span-2 space-y-2">
                            <Label>Name (Task / Activity / Decision)</Label>
                            <Input
                                value={newName}
                                onChange={(e) => setNewName(e.target.value)}
                                placeholder="e.g. Monthly Budget Review"
                                className="bg-white"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Type</Label>
                            <Select value={newType} onValueChange={setNewType}>
                                <SelectTrigger className="bg-white">
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Task">Task</SelectItem>
                                    <SelectItem value="Activity">Activity</SelectItem>
                                    <SelectItem value="Decision">Decision</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                        <Button onClick={handleAdd} disabled={saving}>
                            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
                            Add Item
                        </Button>
                    </div>
                </CardContent>
            </Card>

            <div className="space-y-4">
                <div className="relative">
                    <Search className="absolute left-3 top-3 h-4 w-4 text-muted-foreground" />
                    <Input
                        placeholder="Search items..."
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        className="pl-10 max-w-md"
                    />
                </div>

                <Card>
                    <CardContent className="p-0">
                        <Table>
                            <TableHeader>
                                <TableRow>
                                    <TableHead className="pl-6">Name</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead className="text-right pr-6">Actions</TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {loading && settings.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center py-20">
                                            <Loader2 className="h-8 w-8 animate-spin mx-auto text-muted-foreground" />
                                        </TableCell>
                                    </TableRow>
                                ) : filteredSettings.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={3} className="text-center py-20 text-muted-foreground">
                                            No predefined items found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    filteredSettings.map((item) => (
                                        <TableRow key={item.id} className="group">
                                            <TableCell className="font-medium pl-6">{item.name}</TableCell>
                                            <TableCell>{item.type}</TableCell>
                                            <TableCell className="text-right pr-6">
                                                <div className="flex justify-end gap-1">
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-primary"
                                                        onClick={() => handleEditClick(item)}
                                                    >
                                                        <Edit size={14} />
                                                    </Button>
                                                    <Button
                                                        variant="ghost"
                                                        size="icon"
                                                        className="h-8 w-8 text-destructive"
                                                        onClick={() => handleDelete(item.id)}
                                                    >
                                                        <Trash2 size={14} />
                                                    </Button>
                                                </div>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </CardContent>
                </Card>
            </div>

            {/* Edit Dialog */}
            <Dialog open={isEditOpen} onOpenChange={setIsEditOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Edit Predefined Item</DialogTitle>
                        <DialogDescription>Update the details for this delegation item.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Name (Task / Activity / Decision)</Label>
                            <Input
                                value={editName}
                                onChange={(e) => setEditName(e.target.value)}
                                placeholder="e.g. Monthly Budget Review"
                            />
                        </div>
                        <div className="space-y-2">
                            <Label>Type</Label>
                            <Select value={editType} onValueChange={setEditType}>
                                <SelectTrigger>
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="Task">Task</SelectItem>
                                    <SelectItem value="Activity">Activity</SelectItem>
                                    <SelectItem value="Decision">Decision</SelectItem>
                                </SelectContent>
                            </Select>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setIsEditOpen(false)}>Cancel</Button>
                        <Button onClick={handleUpdate} disabled={saving}>
                            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            Save Changes
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
